import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Download,
  Search,
  ArrowUp,
  ArrowDown,
  X,
  Calendar as CalIcon,
} from "lucide-react";
import { toast } from "sonner";
import { ClickableEmployee, ClickableSKU, ClickableOrder } from "@/components/drilldown/Clickables";
import { useDrillDown } from "@/components/drilldown/DrillDownContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import GlobalFilters from "@/components/CollapsibleFiltersPanel";
import EmployeesPicker from "@/components/EmployeesPicker";
import ColumnFilterPopover from "@/components/ColumnFilterPopover";
import { useFilters } from "@/hooks/useFilters";
import reports from "@/api/reports";
import { formatApiError } from "@/api/client";
import { TX_COLORS } from "@/lib/reportColors";
import {
  PAGE_SIZES,
  COLUMNS,
  TX_TYPES,
  EMPTY_CF,
  isActive,
  countActive,
} from "./_tabla/utils";

export default function Tabla() {
  const { t } = useTranslation();
  const { filters, setFilters, reset, payload } = useFilters();
  const { openProductModal, openOrderModal } = useDrillDown();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState("timestamp");
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");
  const [columnFilters, setColumnFilters] = useState(EMPTY_CF);
  // Per-column draft state — applied on click of "Aplicar".
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Memoised stringified keys keep deps lint-clean and avoid refetches
  // triggered by referential identity churn on every render.
  const payloadKey = useMemo(() => JSON.stringify(payload), [payload]);
  const columnFiltersKey = useMemo(
    () => JSON.stringify(columnFilters),
    [columnFilters]
  );

  useEffect(() => {
    let alive = true;
    setLoading(true);
    reports
      .tabla(payload, {
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_dir: sortDir,
        search,
        column_filters: columnFilters,
      })
      .then((r) => alive && setData(r.data || { items: [], total: 0 }))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // payload + columnFilters tracked indirectly via stable keys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadKey, page, pageSize, sortBy, sortDir, search, columnFiltersKey]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));
  const from = (page - 1) * pageSize + (data.items.length ? 1 : 0);
  const to = (page - 1) * pageSize + data.items.length;
  const activeCount = useMemo(() => countActive(columnFilters), [columnFilters]);

  const onSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortBy(col);
      setSortDir("asc");
    }
    setPage(1);
  };

  const setCol = (patch) => {
    setColumnFilters((cf) => ({ ...cf, ...patch }));
    setPage(1);
  };

  const clearCol = (keys) => {
    const patch = {};
    keys.forEach((k) => {
      patch[k] = Array.isArray(EMPTY_CF[k]) ? [] : "";
    });
    setColumnFilters((cf) => ({ ...cf, ...patch }));
    setPage(1);
  };

  const clearAll = () => {
    setColumnFilters(EMPTY_CF);
    setPage(1);
  };

  const doExport = async (format) => {
    setExporting(true);
    try {
      const resp = await reports.tablaExport(
        payload,
        { sort_by: sortBy, sort_dir: sortDir, search, column_filters: columnFilters },
        format
      );
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `transactions.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${t("common.export")} ${format.toUpperCase()}`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 413) toast.error(t("tabla.exportTooBig"));
      else toast.error(formatApiError(err));
    } finally {
      setExporting(false);
    }
  };

  // ----- Per-column popover bodies -----
  const renderColumnFilter = (col) => {
    const cf = columnFilters;
    switch (col.filter) {
      case "date": {
        const active = isActive(cf, ["date_from", "date_to"]);
        return (
          <ColumnFilterPopover
            active={active}
            testId={`tabla-colfilter-${col.key}`}
            width={300}
          >
            <div className="space-y-2.5">
              <div className="text-xs font-medium">{t("tablaFilters.between")}</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">
                    {t("filters.dateFrom")}
                  </Label>
                  <Input
                    type="date"
                    value={cf.date_from || ""}
                    onChange={(e) => setCol({ date_from: e.target.value })}
                    className="h-8 text-xs"
                    data-testid="tabla-colfilter-date-from"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">
                    {t("filters.dateTo")}
                  </Label>
                  <Input
                    type="date"
                    value={cf.date_to || ""}
                    onChange={(e) => setCol({ date_to: e.target.value })}
                    className="h-8 text-xs"
                    data-testid="tabla-colfilter-date-to"
                  />
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => clearCol(["date_from", "date_to"])}
                className="h-7 w-full text-xs"
                data-testid="tabla-colfilter-date-clear"
              >
                <X className="mr-1 h-3 w-3" />
                {t("tablaFilters.clear")}
              </Button>
            </div>
          </ColumnFilterPopover>
        );
      }
      case "employee": {
        const active = isActive(cf, ["employee_search", "employee_ids"]);
        return (
          <ColumnFilterPopover
            active={active}
            testId={`tabla-colfilter-${col.key}`}
            width={360}
          >
            <div className="space-y-2.5">
              <div className="text-xs font-medium">{t("common.employee")}</div>
              <Input
                value={cf.employee_search}
                onChange={(e) => setCol({ employee_search: e.target.value })}
                placeholder={t("tablaFilters.searchPlaceholder")}
                className="h-8 text-xs"
                data-testid="tabla-colfilter-emp-search"
              />
              <EmployeesPicker
                value={cf.employee_ids || []}
                onChange={(v) => setCol({ employee_ids: v })}
                sourceReport={filters.source_report || undefined}
                departmentIds={filters.department_ids || []}
                placeholder={t("common.all")}
                testId="tabla-colfilter-emp-picker"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => clearCol(["employee_search", "employee_ids"])}
                className="h-7 w-full text-xs"
                data-testid="tabla-colfilter-emp-clear"
              >
                <X className="mr-1 h-3 w-3" />
                {t("tablaFilters.clear")}
              </Button>
            </div>
          </ColumnFilterPopover>
        );
      }
      case "type": {
        const active = isActive(cf, ["transaction_types"]);
        return (
          <ColumnFilterPopover
            active={active}
            testId={`tabla-colfilter-${col.key}`}
          >
            <div className="space-y-2.5">
              <div className="text-xs font-medium">{t("common.type")}</div>
              <div className="space-y-1">
                {TX_TYPES.map((tt) => {
                  const picked = (cf.transaction_types || []).includes(tt);
                  return (
                    <label
                      key={tt}
                      className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs hover:bg-accent/40"
                    >
                      <Checkbox
                        checked={picked}
                        onCheckedChange={(v) => {
                          const set = new Set(cf.transaction_types || []);
                          if (v) set.add(tt);
                          else set.delete(tt);
                          setCol({ transaction_types: Array.from(set) });
                        }}
                        data-testid={`tabla-colfilter-type-${tt}`}
                      />
                      <Badge
                        variant="outline"
                        className="font-mono text-[10px]"
                        style={{
                          borderColor: TX_COLORS[tt] || undefined,
                          color: TX_COLORS[tt] || undefined,
                        }}
                      >
                        {tt}
                      </Badge>
                    </label>
                  );
                })}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => clearCol(["transaction_types"])}
                className="h-7 w-full text-xs"
                data-testid="tabla-colfilter-type-clear"
              >
                <X className="mr-1 h-3 w-3" />
                {t("tablaFilters.clear")}
              </Button>
            </div>
          </ColumnFilterPopover>
        );
      }
      case "item": {
        const active = isActive(cf, ["item_search"]);
        return (
          <ColumnFilterPopover
            active={active}
            testId={`tabla-colfilter-${col.key}`}
          >
            <div className="space-y-2.5">
              <div className="text-xs font-medium">{t("common.item")}</div>
              <Input
                value={cf.item_search}
                onChange={(e) => setCol({ item_search: e.target.value })}
                placeholder={t("tablaFilters.searchPlaceholder")}
                className="h-8 text-xs"
                data-testid="tabla-colfilter-item-search"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => clearCol(["item_search"])}
                className="h-7 w-full text-xs"
                data-testid="tabla-colfilter-item-clear"
              >
                <X className="mr-1 h-3 w-3" />
                {t("tablaFilters.clear")}
              </Button>
            </div>
          </ColumnFilterPopover>
        );
      }
      case "qty": {
        const active = isActive(cf, ["qty_min", "qty_max"]);
        return (
          <ColumnFilterPopover
            active={active}
            testId={`tabla-colfilter-${col.key}`}
          >
            <div className="space-y-2.5">
              <div className="text-xs font-medium">{t("common.qty")}</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">min</Label>
                  <Input
                    type="number"
                    value={cf.qty_min}
                    onChange={(e) => setCol({ qty_min: e.target.value })}
                    className="h-8 text-xs"
                    data-testid="tabla-colfilter-qty-min"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">max</Label>
                  <Input
                    type="number"
                    value={cf.qty_max}
                    onChange={(e) => setCol({ qty_max: e.target.value })}
                    className="h-8 text-xs"
                    data-testid="tabla-colfilter-qty-max"
                  />
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => clearCol(["qty_min", "qty_max"])}
                className="h-7 w-full text-xs"
                data-testid="tabla-colfilter-qty-clear"
              >
                <X className="mr-1 h-3 w-3" />
                {t("tablaFilters.clear")}
              </Button>
            </div>
          </ColumnFilterPopover>
        );
      }
      case "order": {
        const active = isActive(cf, ["order_search"]);
        return (
          <ColumnFilterPopover
            active={active}
            testId={`tabla-colfilter-${col.key}`}
          >
            <div className="space-y-2.5">
              <div className="text-xs font-medium">{t("common.order")}</div>
              <Input
                value={cf.order_search}
                onChange={(e) => setCol({ order_search: e.target.value })}
                placeholder={t("tablaFilters.searchPlaceholder")}
                className="h-8 text-xs"
                data-testid="tabla-colfilter-order-search"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => clearCol(["order_search"])}
                className="h-7 w-full text-xs"
                data-testid="tabla-colfilter-order-clear"
              >
                <X className="mr-1 h-3 w-3" />
                {t("tablaFilters.clear")}
              </Button>
            </div>
          </ColumnFilterPopover>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="tabla-page">
      <div>
        <h1
          className="text-3xl font-semibold tracking-tight"
          data-testid="tabla-title"
        >
          {t("tabla.title")}
        </h1>
        <p className="text-sm text-muted-foreground">{t("tabla.subtitle")}</p>
      </div>

      <GlobalFilters filters={filters} onChange={setFilters} onReset={reset} />

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">{t("common.search")}</Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="tabla-search-input"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder={t("tabla.searchPlaceholder")}
              className="h-9 w-72 pl-8"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{t("tabla.pageSize")}</Label>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => {
              setPageSize(Number(v));
              setPage(1);
            }}
          >
            <SelectTrigger data-testid="tabla-page-size" className="h-9 w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((p) => (
                <SelectItem
                  key={p}
                  value={String(p)}
                  data-testid={`tabla-page-size-${p}`}
                >
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        <Button
          variant="outline"
          size="sm"
          onClick={() => doExport("xlsx")}
          disabled={exporting}
          data-testid="tabla-export-xlsx"
        >
          <Download className="mr-2 h-3.5 w-3.5" />
          {t("tabla.exportXlsx")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => doExport("csv")}
          disabled={exporting}
          data-testid="tabla-export-csv"
        >
          <Download className="mr-2 h-3.5 w-3.5" />
          {t("tabla.exportCsv")}
        </Button>
      </div>

      {/* Active column-filters summary bar (Phase 4 Fix F) */}
      {activeCount > 0 && (
        <div
          className="flex flex-wrap items-center gap-3 rounded-lg border border-primary/40 bg-primary/10 px-4 py-2"
          data-testid="tabla-active-filters-bar"
        >
          <CalIcon className="h-3.5 w-3.5 text-primary" />
          <span className="text-sm font-medium">
            {t("tablaFilters.activeCount", { n: activeCount })}
          </span>
          <div className="flex flex-wrap gap-1.5">
            {(columnFilters.date_from || columnFilters.date_to) && (
              <Badge variant="secondary" className="text-[10px]">
                {t("common.date")}
              </Badge>
            )}
            {(columnFilters.employee_search ||
              (columnFilters.employee_ids?.length || 0) > 0) && (
              <Badge variant="secondary" className="text-[10px]">
                {t("common.employee")}
              </Badge>
            )}
            {(columnFilters.transaction_types?.length || 0) > 0 && (
              <Badge variant="secondary" className="text-[10px]">
                {t("common.type")}
              </Badge>
            )}
            {columnFilters.item_search && (
              <Badge variant="secondary" className="text-[10px]">
                {t("common.item")}
              </Badge>
            )}
            {(columnFilters.qty_min !== "" && columnFilters.qty_min != null) ||
            (columnFilters.qty_max !== "" && columnFilters.qty_max != null) ? (
              <Badge variant="secondary" className="text-[10px]">
                {t("common.qty")}
              </Badge>
            ) : null}
            {columnFilters.order_search && (
              <Badge variant="secondary" className="text-[10px]">
                {t("common.order")}
              </Badge>
            )}
          </div>
          <div className="flex-1" />
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAll}
            className="h-7 text-xs"
            data-testid="tabla-clear-all-filters"
          >
            <X className="mr-1 h-3 w-3" />
            {t("tablaFilters.clearAll")}
          </Button>
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">
            {data.total > 0
              ? t("common.showing", {
                  from,
                  to,
                  total: data.total.toLocaleString(),
                })
              : t("common.no_data")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table data-testid="tabla-table">
              <TableHeader>
                <TableRow>
                  {COLUMNS.map((c) => {
                    const active = sortBy === c.key;
                    return (
                      <TableHead
                        key={c.key}
                        className="select-none text-xs"
                      >
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => onSort(c.key)}
                            className="inline-flex items-center gap-1 hover:text-foreground"
                            data-testid={`tabla-sort-${c.key}`}
                          >
                            {t(c.label)}
                            {active &&
                              (sortDir === "asc" ? (
                                <ArrowUp className="h-3 w-3" />
                              ) : (
                                <ArrowDown className="h-3 w-3" />
                              ))}
                          </button>
                          {renderColumnFilter(c)}
                        </div>
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && data.items.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMNS.length}
                      className="py-12 text-center text-sm text-muted-foreground"
                    >
                      {t("common.loading")}
                    </TableCell>
                  </TableRow>
                ) : data.items.length === 0 ? (
                  <TableRow data-testid="tabla-empty">
                    <TableCell
                      colSpan={COLUMNS.length}
                      className="py-12 text-center text-sm text-muted-foreground"
                    >
                      {t("common.no_data")}
                    </TableCell>
                  </TableRow>
                ) : (
                  data.items.map((r) => (
                    <TableRow key={r.id} data-testid={`tabla-row-${r.id}`}>
                      <TableCell className="font-mono text-xs">
                        {r.timestamp?.slice(0, 10) || ""}
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-xs text-muted-foreground">
                          {r.employee_id}
                        </span>
                        <div className="text-xs">
                          <ClickableEmployee code={r.employee_id} name={r.employee_name}>
                            {r.employee_name}
                          </ClickableEmployee>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className="font-mono text-[10px]"
                          style={{
                            borderColor:
                              TX_COLORS[r.transaction_type] || undefined,
                            color: TX_COLORS[r.transaction_type] || undefined,
                          }}
                        >
                          {r.transaction_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        <ClickableSKU sku={r.item} onOpen={openProductModal}>
                          {r.item}
                        </ClickableSKU>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.qty}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        <ClickableOrder orderId={r.order_id} onOpen={openOrderModal}>
                          {r.order_id}
                        </ClickableOrder>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="text-xs text-muted-foreground">
              {t("common.page_of", { page, total: totalPages })}
            </div>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="tabla-prev-page"
              >
                {t("common.previous")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                data-testid="tabla-next-page"
              >
                {t("common.next")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
